import type { Navigator, Screen } from "../app/screen";
import { Api } from "../net/api";
import { pageShell } from "../ui/page";
import { audio } from "../fx/audio";

/**
 * THE CAMPAIGN HUB - choose which campaign, then play it.
 *
 * 🚨 **THIS PAGE EXISTED AND I DELETED IT BY ACCIDENT.** Alex, 2026-08-15: *"with the campaign mode
 * we are missing a page that we used to have - you click campaign and a page opens that has the
 * Ascent campaign selection and the Five Realms campaign selection."*
 *
 * What happened: the CAMPAIGN plate used to open a chooser. When the FIVE REALMS screen was built I
 * repointed the plate straight at it, wrote a comment saying the Ascent was *"reached from inside
 * it, as one ladder among the realms"*, and never built that link - so one whole campaign mode
 * became unreachable and the chooser stopped existing. Two losses from one edit, and neither was
 * visible to a gate that asks whether screens WORK.
 *
 * ⭐ **A HUB IS THE RIGHT SHAPE BECAUSE THERE ARE GENUINELY THREE CAMPAIGNS**, and they differ in
 * the one way a player cares about - whose deck they play:
 *
 *   THE ASCENT       five champions in order, YOUR deck        (the gauntlet)
 *   THE FIVE REALMS  35 stages across five realms, YOUR deck   (the realm campaign)
 *   HERO TRIALS      eight stages, the HERO'S OWN deck         (where card rewards live)
 *
 * ⚠️ **EACH ROW STATES WHOSE DECK IT USES.** The hero trials take no deck - that is the original's
 * own design and it is what makes the difficulty identical for everyone - and a player who has just
 * built a deck will read a missing picker as a bug unless they were told.
 */

interface Row {
  readonly id: string;
  readonly screen: string;
  readonly title: string;
  readonly line: string;
  readonly deck: string;
}

const MODES: readonly Row[] = [
  {
    id: "ascent",
    screen: "campaign",
    title: "THE ASCENT",
    line: "Five champions, one after another. Each plays differently and each is harder than the "
        + "last.",
    deck: "Your deck",
  },
  {
    id: "realms",
    screen: "campaigns",
    title: "THE FIVE REALMS",
    line: "Thirty-five trials across the realms, each with its own handicap and three stars to take.",
    deck: "Your deck",
  },
  {
    id: "hero",
    screen: "herocampaign",
    title: "HERO TRIALS",
    line: "Learn a hero's class over eight stages, and keep the cards you win.",
    deck: "The hero's own deck - the same for every player",
  },
];

export const CampaignHubScreen: Screen = {
  id: "campaignhub",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "CAMPAIGN", "three ways to play alone");
    host.append(shell.el);

    const list = document.createElement("div");
    list.className = "hub";
    shell.body.append(list);

    // Progress per mode, so the hub says where you are rather than just where you can go. Each is
    // independent: a failure to read one must not blank the others.
    const progress = new Map<string, string>();
    await Promise.all([
      Api.campaignProgress().then((c) => {
        const q = (c as { quadrants?: Array<{ stages?: Array<{ isCompleted?: boolean }> }> })
          .quadrants ?? [];
        const all = q.flatMap((x) => x.stages ?? []);
        progress.set("realms", `${all.filter((s) => s.isCompleted).length} of ${all.length} cleared`);
      }).catch(() => { /* the row still opens; only its subtitle is missing */ }),
      Api.gauntletProgress().then((g) => {
        const lv = (g as { levels?: Array<{ isCompleted?: boolean }> }).levels ?? [];
        progress.set("ascent", `${lv.filter((l) => l.isCompleted).length} of ${lv.length} cleared`);
      }).catch(() => {}),
      Api.heroCampaignProgress().then((h) => {
        const st = (h as { stages?: Array<{ isCompleted?: boolean }> }).stages ?? [];
        progress.set("hero", `${st.filter((s) => s.isCompleted).length} of ${st.length} cleared`);
      }).catch(() => {}),
    ]);

    for (const m of MODES) {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "hub__row";
      row.dataset["label"] = m.title;

      const head = document.createElement("span");
      head.className = "hub__title";
      head.textContent = m.title;

      const done = document.createElement("span");
      done.className = "hub__progress";
      done.textContent = progress.get(m.id) ?? "";

      const line = document.createElement("span");
      line.className = "hub__line";
      line.textContent = m.line;

      // ⭐ WHOSE DECK. The one difference between these modes that changes what a player does next.
      const deck = document.createElement("span");
      deck.className = "hub__deck";
      deck.textContent = m.deck;

      row.append(head, done, line, deck);
      row.addEventListener("click", () => { audio.play("click", 0.6); nav.go(m.screen); });
      list.append(row);
    }
  },
};
